import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Slide,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { TransitionProps } from '@material-ui/core/transitions';
import { Close as CloseIcon, Save as SaveIcon } from '@material-ui/icons';
import {
  ForumOutlined as ForumOutlinedIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  NoteAltOutlined as NoteAltIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  FormatListBulletedOutlined as FormatListBulletedOutlinedIcon,
  AccountTreeOutlined as AccountTreeOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  EventNote as EventNoteIcon,
} from '@mui/icons-material';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { SubtaskItemStatusLabel } from '../../../../constants';
import { ISubtask } from '../../../../models/subtask.model';
import { AppState } from '../../../../state/configureStore';
import {
  clearSubtaskItemSection,
  fetchSubtask,
  SubtaskState,
  updateSubtask,
} from '../../../../state/subtask';
import {
  clearSubtaskItem,
  fetchSubtaskItemListBySubtaskId,
} from '../../../../state/subtaskItem';
import { fetchAllSubtaskTypeList, SubtaskTypeState } from '../../../../state/subtaskType';
import { TaskState } from '../../../../state/task';

import { User } from '../../../../state/user';
import { Loading } from '../../../components';
import UserComboBox from '../../../components/UserComboBox';
import { TaskActivityComment, TaskActivityList } from '../taskActivity';
import SubtaskItemTable from './SubtaskItemTable';
import {
  Card as MUICard,
  Paper as MUIPaper,
  CardContent as MUICardContent,
  Stack as MUIStack,
  Box as MUIBox,
  Typography as MUITypography,
  Grid as MUIGrid,
} from '@mui/material';
import CustomCaption from '../../../components/CustomCaption';
import { alpha } from '@material-ui/core';

const sectionCardSx = {
  borderRadius: 3,
  borderColor: 'rgba(124, 58, 237, 0.12)',
  backgroundColor: 'rgba(255,255,255,0.9)',
  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)',
};

const fieldBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appBar: {
      position: 'relative',
      background:
        'linear-gradient(135deg, #1d4ed8 0%, #2563eb 52%, rgb(82, 133, 243) 100%)',
      boxShadow: '0 20px 45px rgba(15, 23, 42, 0.28)',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
    },
    toolbar: {
      minHeight: 84,
      // paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(2.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        minHeight: 76,
        paddingRight: theme.spacing(1.5),
      },
    },
    headerContent: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      minWidth: 0,
    },
    closeButton: {
      width: 46,
      height: 46,
      borderRadius: 14,
      backgroundColor: 'rgba(255,255,255,0.1)',
      border: '1px solid rgba(255,255,255,0.12)',
      transition: 'all 160ms ease',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.18)',
        transform: 'translateY(-1px)',
      },
    },
    titleWrap: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    eyebrow: {
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      color: 'rgba(224, 231, 255, 0.72)',
    },
    title: {
      color: '#f8fafc',
      fontWeight: 700,
      lineHeight: 1.15,
      letterSpacing: '-0.01em',
    },
    titleSubtitle: {
      color: 'rgba(226, 232, 240, 0.82)',
      lineHeight: 1.35,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    headerBadge: {
      padding: theme.spacing(0.875, 1.5),
      borderRadius: 999,
      fontSize: '0.76rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: '#eff6ff',
      background: 'rgba(255,255,255,0.1)',
      border: '1px solid rgba(255,255,255,0.14)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    formActionShell: {
      display: 'flex',
      gap: theme.spacing(1),
      padding: theme.spacing(0.75),
      borderRadius: 999,
      border: '1px solid rgba(148, 163, 184, 0.18)',
      background:
        'linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.88) 100%)',
      boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
    },
    secondaryActionButton: {
      minHeight: 44,
      padding: theme.spacing(0, 2),
      borderRadius: 999,
      textTransform: 'none',
      fontWeight: 700,
      borderColor: 'rgba(148, 163, 184, 0.28)',
      color: '#334155',
      backgroundColor: 'rgba(255,255,255,0.72)',
      '&:hover': {
        borderColor: 'rgba(37, 99, 235, 0.3)',
        backgroundColor: '#ffffff',
      },
    },
    primaryActionButton: {
      minHeight: 44,
      padding: theme.spacing(0, 2.5),
      borderRadius: 999,
      textTransform: 'none',
      fontWeight: 700,
      background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 52%, #60a5fa 100%)',
      boxShadow: '0 14px 30px rgba(37, 99, 235, 0.28)',
      '&:hover': {
        background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 52%, #3b82f6 100%)',
        boxShadow: '0 16px 32px rgba(37, 99, 235, 0.34)',
      },
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: theme.spacing(1, 2),
      marginTop: '-30px',
      marginRight: '-25px',
    },
    tab: {
      minHeight: 0,
      '& .MuiTabs-scroller': {
        paddingBottom: theme.spacing(0.5),
        paddingTop: theme.spacing(0.5),
      },
      '& .MuiTabs-flexContainer': {
        gap: theme.spacing(1),
      },
      '& .MuiTabs-indicator': {
        display: 'none',
      },
      '& .MuiTab-root': {
        minHeight: 64,
        minWidth: 160,
        padding: theme.spacing(1.25, 1.5),
        borderRadius: 16,
        overflow: 'hidden',
        border: '1px solid rgba(148, 163, 184, 0.18)',
        backgroundColor: 'rgba(255,255,255,0.72)',
        textTransform: 'none',
        color: '#475569',
        boxShadow:
          'inset 0 1px 0 rgba(255,255,255,0.55), 0 2px 6px rgba(15, 23, 42, 0.03)',
        transition:
          'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background-color 180ms ease',
        '&:hover': {
          backgroundColor: '#ffffff',
          borderColor: 'rgba(59, 130, 246, 0.22)',
          // boxShadow:
          //   '0 8px 18px rgba(148, 163, 184, 0.14), 0 2px 6px rgba(59, 130, 246, 0.08)',
          transform: 'translateY(-1px)',
        },
      },
      '& .MuiTab-root.Mui-selected': {
        color: '#0f172a',
        borderColor: 'rgba(37, 99, 235, 0.48)',
        background:
          'linear-gradient(135deg, rgba(239,246,255,0.98) 0%, rgba(238,242,255,0.98) 100%)',
        // boxShadow:
        //   '0 10px 22px rgba(37, 99, 235, 0.14), 0 3px 8px rgba(37, 99, 235, 0.1), 0 0 0 1px rgba(191, 219, 254, 0.9) inset',
      },
      '& .MuiTab-root.Mui-selected $tabIcon': {
        color: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.12)',
      },
      '& .MuiTab-root.Mui-selected $tabHint': {
        color: '#1d4ed8',
      },
      '& .MuiTab-root.Mui-selected $tabCount': {
        backgroundColor: '#2563eb',
        color: '#ffffff',
        boxShadow: '0 8px 18px rgba(37, 99, 235, 0.26)',
      },
      [theme.breakpoints.down('sm')]: {
        '& .MuiTab-root': {
          minWidth: 148,
        },
      },
    },
    tabSection: {
      padding: theme.spacing(1),
      borderRadius: 20,
      overflow: 'hidden',
      border: `1px solid ${alpha('#7c3aed', 0.12)}`,
      background:
        'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.9) 100%)',
      boxShadow: '0 14px 32px rgba(15, 23, 42, 0.07)',
    },
    tabLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      width: '100%',
    },
    tabIcon: {
      display: 'grid',
      placeItems: 'center',
      width: 28,
      height: 28,
      borderRadius: 10,
      color: '#475569',
      backgroundColor: 'rgba(148, 163, 184, 0.14)',
      transition: 'all 180ms ease',
    },
    tabTextGroup: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      lineHeight: 1.15,
    },
    tabText: {
      fontSize: '0.92rem',
      fontWeight: 700,
      letterSpacing: 0,
    },
    tabHint: {
      fontSize: '0.72rem',
      color: '#64748b',
    },
    tabCount: {
      marginLeft: 'auto',
      minWidth: 28,
      height: 28,
      padding: theme.spacing(0, 1),
      borderRadius: 999,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(99, 102, 241, 0.08)',
      color: '#4338ca',
      fontSize: '0.8rem',
      fontWeight: 700,
      transition: 'all 180ms ease',
    },
  }),
);

const detailTabs = [
  {
    label: 'Items',
    hint: 'Supporting entries',
    icon: <FormatListBulletedOutlinedIcon fontSize="small" />,
  },
  {
    label: 'Activities',
    hint: 'Updates and comments',
    icon: <ForumOutlinedIcon fontSize="small" />,
  },
];

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return (
    <Slide direction="up" ref={ref} {...props} timeout={{ enter: 400, exit: 400 }} />
  );
});

const SubtaskEditSchema = Yup.object().shape({
  subject: Yup.string().required('Subject is required'),
  description: Yup.string().nullable(),
  dueDate: Yup.string()
    .nullable()
    .test(
      'dueDateFormat',
      'Invalid date format',
      (value) => !isNaN(new Date(value).getTime()),
    )
    .required('Due Date is required'),
  smSubtaskAssignees: Yup.array()
    .of(
      Yup.object().shape({
        userId: Yup.number(),
      }),
    )
    .required('Assignee is required'),
  subtaskItems: Yup.array().of(
    Yup.object().shape({
      custReqNo: Yup.number(),
    }),
  ),
});

interface SubtaskEditDialogProps {
  handleClose: () => void;
  open: boolean;
  subtaskId: number | null;
}

enum SubtaskFormTabs {
  item,
  activity,
}

interface ISubtaskEditDialogValues extends ISubtask {
  subtaskAssigneeUsers?: User[];
}

const SubtaskEditDialog: React.FC<SubtaskEditDialogProps> = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { handleClose, open, subtaskId } = props;
  const { item: task } = useSelector<AppState, TaskState>((state) => state.task);
  const { item: subtask, loading: subtaskIsLoading } = useSelector<
    AppState,
    SubtaskState
  >((state) => state.subtask);
  const { items: subtaskTypes, loading: subtaskTypeIsLoading } = useSelector<
    AppState,
    SubtaskTypeState
  >((state) => state.subtaskType);

  const [tabIndex, setTabIndex] = useState(SubtaskFormTabs.item);

  const {
    id,
    dueDate,
    subject = '',
    description = '',
    status,
    useCommonStatus,
    smSubtaskTypeId,
    smSubtaskAssignees = [],
  } = subtask;

  const subtaskAssigneeUsers = useMemo(() => {
    return smSubtaskAssignees.map((smSubtaskAssignee) => smSubtaskAssignee.user);
  }, [smSubtaskAssignees]);

  const handleSubmit = (
    values: ISubtaskEditDialogValues,
    actions: FormikHelpers<ISubtaskEditDialogValues>,
  ) => {
    const updateValue = { ...values, id, smTaskId: task.id };
    dispatch(updateSubtask(updateValue));
    actions.setSubmitting(false);
  };

  useEffect(() => {
    if (subtaskId) {
      dispatch(fetchSubtask(subtaskId));
      dispatch(fetchSubtaskItemListBySubtaskId(subtaskId));
    }
    dispatch(fetchAllSubtaskTypeList());

    function cleanUp() {
      dispatch(clearSubtaskItemSection());
      dispatch(clearSubtaskItem());
      // dispatch(clearTaskActivity());
    }

    return cleanUp();
  }, [dispatch, subtaskId]);

  return (
    <>
      <Dialog
        fullScreen
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <AppBar className={classes.appBar}>
          <Toolbar className={classes.toolbar}>
            <Box className={classes.headerContent}>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleClose}
                className={classes.closeButton}
              >
                <CloseIcon />
              </IconButton>
              <Box className={classes.titleWrap}>
                <Typography variant="h6" className={classes.title}>
                  Edit Subtask
                </Typography>
                <Typography variant="body2" className={classes.titleSubtitle}>
                  Refine the subtask details, ownership, and timeline in one place.
                </Typography>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
        <DialogContent>
          <MUIPaper
            elevation={0}
            sx={{
              p: { xs: 1.5, md: 2.5 },
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'rgba(124, 58, 237, 0.08)',
              background:
                'radial-gradient(circle at top, rgba(124,58,237,0.06), transparent 35%), linear-gradient(180deg, #fbfbff 0%, #f6f8ff 100%)',
              boxShadow: '0 18px 40px rgba(15, 23, 42, 0.06)',
              marginTop: 4,
            }}
          >
            <Loading isLoading={subtaskTypeIsLoading || subtaskIsLoading}>
              <Formik
                enableReinitialize={true}
                initialValues={{
                  subject,
                  dueDate,
                  description,
                  status,
                  useCommonStatus,
                  smSubtaskTypeId,
                  subtaskAssigneeUsers,
                  smSubtaskAssignees,
                }}
                validationSchema={SubtaskEditSchema}
                onSubmit={handleSubmit}
                validateOnChange={false}
              >
                {({ values, handleChange, errors, submitForm, setFieldValue }) => {
                  return (
                    <Form>
                      {/* Subtask Header Section */}
                      <MUIBox
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          mb: 1.5,
                        }}
                      >
                        <Typography variant="h5">
                          Editing Subtask: {subtask.subject}
                        </Typography>
                        <MUIBox className={classes.formActionShell}>
                          <Button
                            color="primary"
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            className={classes.secondaryActionButton}
                            onClick={() => {
                              handleClose();
                            }}
                          >
                            Close
                          </Button>
                          <Button
                            color="primary"
                            variant="contained"
                            className={classes.primaryActionButton}
                            onClick={() => {
                              submitForm();
                            }}
                            startIcon={<SaveIcon />}
                          >
                            Update
                          </Button>
                        </MUIBox>
                      </MUIBox>

                      {/* Subtask Information Section */}
                      <MUICard variant="outlined" sx={{ ...sectionCardSx }}>
                        <MUICardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                          <MUIStack spacing={2.5}>
                            <MUIBox
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                pb: 1.5,
                                borderBottom: '1px solid rgba(124, 58, 237, 0.08)',
                              }}
                            >
                              <MUIBox
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 2.5,
                                  display: 'grid',
                                  placeItems: 'center',
                                  color: '#7c3aed',
                                  background: 'rgba(124, 58, 237, 0.10)',
                                }}
                              >
                                <Inventory2OutlinedIcon fontSize="small" />
                              </MUIBox>
                              <MUIBox>
                                <MUITypography
                                  variant="h6"
                                  sx={{ fontWeight: 700, color: '#4338ca' }}
                                >
                                  Subtask Information
                                </MUITypography>
                                <MUITypography
                                  variant="body2"
                                  sx={{ color: 'text.secondary' }}
                                >
                                  Manage subtask details, assignees, and due date here.
                                </MUITypography>
                              </MUIBox>
                            </MUIBox>

                            {/* Subtask Fields */}
                            <MUIGrid container spacing={1}>
                              {/* Subject */}
                              <MUIGrid item xs={12} md={6} sx={fieldBlockSx}>
                                <CustomCaption
                                  caption={'Subject'}
                                  icon={
                                    <NoteAltIcon
                                      fontSize="small"
                                      style={{ color: '#4f46e5' }}
                                    />
                                  }
                                />
                                <TextField
                                  name="subject"
                                  type="input"
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  value={values.subject || ''}
                                  onChange={handleChange}
                                  error={!!errors.subject}
                                  helperText={errors.subject}
                                />
                              </MUIGrid>
                              {/* Subtask Type */}
                              <MUIGrid item xs={12} sm={4} md={2} sx={fieldBlockSx}>
                                <CustomCaption
                                  caption={'Subtask Type'}
                                  icon={
                                    <AccountTreeOutlinedIcon
                                      fontSize="small"
                                      style={{ color: '#7c3aed' }}
                                    />
                                  }
                                />
                                <FormControl fullWidth variant="outlined" margin="dense">
                                  <Select
                                    fullWidth
                                    variant="outlined"
                                    value={values.smSubtaskTypeId || ''}
                                    readOnly={true}
                                  >
                                    {subtaskTypes.map((item) => {
                                      return (
                                        <MenuItem key={item.id} value={item.id}>
                                          {item.name}
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>
                              </MUIGrid>
                              {/* Due Date */}
                              <MUIGrid item xs={12} sm={4} md={2} sx={fieldBlockSx}>
                                <CustomCaption
                                  caption={'Due Date'}
                                  icon={
                                    <EventNoteIcon
                                      fontSize="small"
                                      style={{ color: '#ef4444' }}
                                    />
                                  }
                                />
                                <KeyboardDatePicker
                                  id="date-picker-dialog"
                                  inputVariant="outlined"
                                  margin="dense"
                                  format="yyyy-MM-dd"
                                  value={values.dueDate}
                                  onChange={(selectedDate, selectedStringDate) => {
                                    setFieldValue('dueDate', selectedStringDate);
                                  }}
                                  error={!!errors.dueDate}
                                  helperText={errors.dueDate}
                                  fullWidth
                                />
                              </MUIGrid>

                              {/* Status */}
                              <MUIGrid item xs={12} sm={4} md={2} sx={fieldBlockSx}>
                                <CustomCaption
                                  caption={'Status'}
                                  icon={
                                    <FormatListBulletedOutlinedIcon
                                      fontSize="small"
                                      style={{ color: '#0ea5e9' }}
                                    />
                                  }
                                />
                                <TextField
                                  name="status"
                                  type="input"
                                  variant="outlined"
                                  margin="dense"
                                  value={
                                    values.status
                                      ? SubtaskItemStatusLabel[values.status]
                                      : ''
                                  }
                                  disabled
                                  fullWidth
                                />
                              </MUIGrid>

                              {/* Description */}
                              <MUIGrid item xs={12} sx={fieldBlockSx}>
                                <CustomCaption
                                  caption={'Description'}
                                  icon={
                                    <DescriptionOutlinedIcon
                                      fontSize="small"
                                      style={{ color: '#0891b2' }}
                                    />
                                  }
                                />
                                <TextField
                                  name="description"
                                  type="input"
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  value={values.description || ''}
                                  onChange={handleChange}
                                  error={!!errors.description}
                                  helperText={errors.description}
                                  multiline
                                  rows="3"
                                />
                              </MUIGrid>

                              {/* Assignee Users */}
                              <MUIGrid item xs={12} sx={fieldBlockSx}>
                                <CustomCaption
                                  caption={'Assignee Users'}
                                  icon={
                                    <PersonOutlineIcon
                                      fontSize="small"
                                      style={{ color: '#f59e0b' }}
                                    />
                                  }
                                />
                                <UserComboBox
                                  handleChange={(users: User[]) => {
                                    setFieldValue(
                                      'subtaskAssigneeUsers',
                                      users ? users : undefined,
                                    );
                                    setFieldValue(
                                      'smSubtaskAssignees',
                                      users
                                        ? users.map((user) => {
                                            return { userId: user.id };
                                          })
                                        : [],
                                    );
                                  }}
                                  filterField="FullNameCompanyDepartment.Contains"
                                  selectedValue={values.subtaskAssigneeUsers as User[]}
                                  error={!!errors.smSubtaskAssignees}
                                  helperText={errors.smSubtaskAssignees as string}
                                  label="Assignee"
                                  variant="outlined"
                                  isMulti
                                />
                              </MUIGrid>

                              {/* Use Common Status Not Show*/}
                              {/* <MUIGrid item xs={2} sx={fieldBlockSx}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={values.useCommonStatus}
                                      onChange={(event) => {
                                        setFieldValue(
                                          'useCommonStatus',
                                          event.target.checked,
                                        );
                                      }}
                                      name="useCommonStatus"
                                      color="primary"
                                    />
                                  }
                                  label="Use Common Status"
                                />
                              </MUIGrid> */}
                            </MUIGrid>
                          </MUIStack>
                        </MUICardContent>
                      </MUICard>
                    </Form>
                  );
                }}
              </Formik>

              {/* Subtask Item Section */}
              <MUICard variant="outlined" sx={{ ...sectionCardSx, mt: 3 }}>
                <MUICardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                  <MUIStack spacing={2.5}>
                    <MUIBox
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        pb: 1.5,
                        borderBottom: '1px solid rgba(124, 58, 237, 0.08)',
                      }}
                    >
                      <MUIBox
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2.5,
                          display: 'grid',
                          placeItems: 'center',
                          color: '#2563eb',
                          background: 'rgba(37, 99, 235, 0.10)',
                        }}
                      >
                        <Inventory2OutlinedIcon fontSize="small" />
                      </MUIBox>
                      <MUIBox>
                        <MUITypography
                          variant="h6"
                          sx={{ fontWeight: 700, color: '#2563eb' }}
                        >
                          Subtask Items Information
                        </MUITypography>
                        <MUITypography variant="body2" sx={{ color: 'text.secondary' }}>
                          Manage subtask item details and status here. You can also track
                          the activity of each item in the activity tab.
                        </MUITypography>
                      </MUIBox>
                    </MUIBox>

                    {/* Tab Details */}
                    <MUIGrid container spacing={1}>
                      <MUIGrid item xs={12}>
                        <MUIBox className={classes.tabSection}>
                          <Tabs
                            value={tabIndex}
                            indicatorColor="primary"
                            textColor="primary"
                            onChange={(event, newIndex) => setTabIndex(newIndex)}
                            variant="scrollable"
                            scrollButtons="auto"
                            className={classes.tab}
                          >
                            {detailTabs.map((tab) => (
                              <Tab
                                key={tab.label}
                                label={
                                  <span className={classes.tabLabel}>
                                    <span className={classes.tabIcon}>{tab.icon}</span>
                                    <span className={classes.tabTextGroup}>
                                      <span className={classes.tabText}>{tab.label}</span>
                                      <span className={classes.tabHint}>{tab.hint}</span>
                                    </span>
                                  </span>
                                }
                              />
                            ))}
                            {/* <Tab label="Log Works" /> */}
                          </Tabs>
                          {tabIndex === SubtaskFormTabs.item && <SubtaskItemTable />}
                          {tabIndex === SubtaskFormTabs.activity && subtask.smTaskId && (
                            <Box p={2}>
                              <TaskActivityComment
                                smTaskId={subtask.smTaskId}
                                smSubtaskId={subtask.id}
                              />
                              <TaskActivityList
                                smTaskId={subtask.smTaskId}
                                smSubtaskId={subtask.id}
                              />
                            </Box>
                          )}
                        </MUIBox>
                      </MUIGrid>
                    </MUIGrid>
                  </MUIStack>
                </MUICardContent>
              </MUICard>
            </Loading>
          </MUIPaper>

          {/* <PageContainer>
            <Card>
              <Tabs
                value={tabIndex}
                indicatorColor="primary"
                textColor="primary"
                onChange={(event, newIndex) => setTabIndex(newIndex)}
                variant="scrollable"
                className={classes.tab}
              >
                <Tab label="items" />
                <Tab label="Activities" />
              </Tabs>
            </Card>
            {tabIndex === SubtaskFormTabs.item && <SubtaskItemTable />}
            {tabIndex === SubtaskFormTabs.activity && subtask.smTaskId && (
              <Box p={2}>
                <TaskActivityComment
                  smTaskId={subtask.smTaskId}
                  smSubtaskId={subtask.id}
                />
                <TaskActivityList smTaskId={subtask.smTaskId} smSubtaskId={subtask.id} />
              </Box>
            )}
          </PageContainer> */}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SubtaskEditDialog;
